import React, { useMemo } from "react";
import { useNavigate } from "react-router-dom";
import toast from "react-hot-toast";
import { Badge, Group } from "@mantine/core";
import { PageList, ENUM_FORM_DISPLAY } from "../../components/pageList";
import ActionCellRender from "../../components/actionCellRender";
import { IconPencil, IconCircleCheck, IconPlayerPlay, IconCopy } from "@tabler/icons";
import { useServerApi } from "../../hooks/userServerApi";
import { useTabNavigate } from "../../hooks/useTabNavigate";
import { NOTIFICATION_ENTITIES, NOTIFICATION_EVENTS, NOTIFICATION_CHANNELS, channelLabel } from "../../data/notificationEntities";

const apiEntity = "notificationRule";

const entityLabel = (value) =>
  NOTIFICATION_ENTITIES.find((e) => e.value === value)?.label ?? value;

const eventLabel = (value) => {
  const normalized = value === "FIELD_CHANGE" ? "UPDATE" : value;
  return NOTIFICATION_EVENTS.find((e) => e.value === normalized)?.label ?? value;
};

const formSchema = {
  title: "Notification Rule",
  apiEntity,
  display: { mode: ENUM_FORM_DISPLAY.MODAL, size: "sm" },
  initialValues: { name: "" },
  layout: { fields: [] },
};

const NotificationRules = () => {
  const navigate = useNavigate();
  const [api] = useServerApi();
  const tabNav = useTabNavigate();

  const listActions = useMemo(
    () => [
      {
        label: "New Rule",
        onClick: () => navigate("/management/notificationRules/new"),
        requiresWrite: true,
      },
    ],
    [navigate]
  );

  const tableSchema = useMemo(
    () => ({
      columns: [
        { field: "code", headerName: "Code", width: 140 },
        { field: "name", headerName: "Rule Name", width: 220 },
        {
          field: "triggerConfig.targetEntity",
          headerName: "Entity",
          width: 120,
          cellRender: (col, data) => entityLabel(data?.triggerConfig?.targetEntity),
        },
        {
          field: "triggerConfig.event",
          headerName: "Event",
          width: 130,
          cellRender: (col, data) => (
            <Badge
              size="sm"
              color={
                ["UPDATE", "FIELD_CHANGE"].includes(data?.triggerConfig?.event)
                  ? "blue"
                  : data?.triggerConfig?.event === "CREATE"
                    ? "teal"
                    : "grape"
              }
            >
              {eventLabel(data?.triggerConfig?.event)}
            </Badge>
          ),
        },
        {
          field: "triggerConfig.watchFields",
          headerName: "Watch Fields",
          width: 160,
          cellRender: (col, data) =>
            (data?.triggerConfig?.watchFields ?? [])
              .map((w) => w.field)
              .filter(Boolean)
              .join(", "),
        },
        {
          field: "channels",
          headerName: "Channels",
          width: 140,
          cellRender: (col, data) => {
            const channels = data?.channels?.length
              ? data.channels
              : data?.channel
                ? [data.channel]
                : [];
            return (
              <Group spacing={4}>
                {channels.map((ch) => (
                  <Badge key={ch} size="sm" variant="outline" color={ch === "IN_APP" ? "cyan" : "blue"}>
                    {channelLabel(ch)}
                  </Badge>
                ))}
              </Group>
            );
          },
        },
        {
          field: "status",
          headerName: "Status",
          width: 100,
          cellRender: (col, data) => (
            <Badge size="sm" color={data?.status === "ACTIVE" ? "green" : "gray"}>
              {data?.status}
            </Badge>
          ),
        },
        {
          cellRender: ActionCellRender,
          isAction: true,
          actions: [
            { name: "edit", label: "Edit", icon: IconPencil },
            { name: "toggle", label: "Toggle status", icon: IconCircleCheck },
            { name: "test", label: "Test", icon: IconPlayerPlay },
            { name: "duplicate", label: "Duplicate", icon: IconCopy },
          ],
        },
      ],
      onActionBtnClickCustom: async ({ action, data, fetchData }) => {
        if (action === "edit") {
          tabNav.open({
            pathname: `/management/notificationRules/${data._id}`,
            label: data.name ? `Rule - ${data.name}` : `Rule - ${data.code}`,
          });
          return;
        }
        if (action === "toggle") {
          const res = await api.Notification.toggleRuleStatus(data._id);
          if (res?.success) {
            toast.success(data.status === "ACTIVE" ? "Rule deactivated" : "Rule activated");
            fetchData?.();
          }
          return;
        }
        if (action === "test") {
          const res = await api.Notification.testRule(data._id);
          if (res?.success) {
            toast.success("Test notification sent");
          }
          return;
        }
        if (action === "duplicate") {
          const { _id, code, createdAt, updatedAt, ...rest } = data;
          const payload = {
            ...rest,
            name: `${rest.name} (Copy)`,
            status: "INACTIVE",
          };
          const res = await api.add({ apiEntity, values: payload });
          if (res?.success) {
            toast.success("Rule duplicated");
            fetchData?.();
          }
        }
      },
    }),
    [api, tabNav]
  );

  return (
    <PageList
      apiEntity={apiEntity}
      title="Notification Rules"
      formSchema={formSchema}
      showAddButton={false}
      customActions={listActions}
      tableSchema={tableSchema}
      initSort={{ by: "createdAt", order: "desc" }}
    />
  );
};

export default NotificationRules;
